<template>
  <div class="FinancesInvoicesCompanySpecialisationsTable finances-invoices-table">
    <PanelHeader
      title="Specialisations"
      :isOpen="show"
      :onToggleButtonClick="togglePanel"
      titleClassName="col-md-8"
      buttonsWrapperClassName="col-md-4"
    >
      <AddItemButton :onClick="addItem" />
    </PanelHeader>
    <b-collapse class="mx-3" style="position: relative;" v-model="show">
      <b-table :fields="fields" :items="items" borderless class="table-fixed">
        <template #cell(specialisation_id)="{ item, index }">
          <div>
            <multiselect
              :class="{ 'multiselect__single-line': true, 'is-invalid': isSpecialisationFieldInvalid(item) || getBackendError(index, 'specialisation_id'), 'is-valid': shouldShowGreenBorder(index, 'specialisation_id') }"
              :value="getSpecialisations.find(i => i.id === item.specialisation_id)"
              :options="getSpecialisations"
              @input="(selected) => {
                dataTouched(index, 'specialisation_id');
                clearBackendError(index, 'specialisation_id');
                item.specialisation_id = selected.id;
              }"
              track-by="id"
              label="type_display_name"
              :showLabels="false"
            />
            <FieldErrorMessage
              :backend-error="getBackendError(index, 'specialisation_id')"
              :show-frontend-error="isSpecialisationFieldInvalid(item)"
              frontend-message="Field is required"
            />
          </div>
        </template>
        <template #cell(hours)="{ item, index }">
          <div>
            <b-form-input
              type="number"
              step="0.01"
              no-wheel
              v-model="item.hours"
              :class="{ 'is-invalid': isHoursFieldInvalid(item) || getBackendError(index, 'hours'), 'is-valid': shouldShowGreenBorder(index, 'hours') }"
              @input="dataTouched(index, 'hours'); clearBackendError(index, 'hours')"
            />
            <FieldErrorMessage
              :backend-error="getBackendError(index, 'hours')"
              :show-frontend-error="isHoursFieldInvalid(item)"
              frontend-message="Field is required"
            />
          </div>
        </template>
        <template #cell(cost)="{ item, index }">
          <div>
            <b-form-input
              type="number"
              no-wheel
              v-model="item.cost"
              :class="{ 'is-invalid': isCostFieldInvalid(item) || getBackendError(index, 'cost'), 'is-valid': shouldShowGreenBorder(index, 'cost') }"
              @input="dataTouched(index, 'cost'); clearBackendError(index, 'cost')"
            />
            <FieldErrorMessage
              :backend-error="getBackendError(index, 'cost')"
              :show-frontend-error="isCostFieldInvalid(item)"
              frontend-message="Field is required"
            />
          </div>
        </template>
        <template #cell(actions)="data">
         <b-button variant="danger" size="sm w-100" @click.prevent="removeItem(data)">
           <font-awesome-icon icon="trash" />
         </b-button>
        </template>
      </b-table>
    </b-collapse>

    <PanelDelimiter />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
import PanelHeader from '@components/invoices/form/PanelHeader.vue'
import AddItemButton from '@components/modals/FinanceInvoicesForm/components/AddItemButton.vue'
import PanelDelimiter from '@components/modals/FinanceInvoicesForm/components/PanelDelimiter.vue'
import FieldErrorMessage from '@components/forms/FieldErrorMessage.vue'

const defaultItem = {
  'specialisation_id': null,
  'hours': null,
  'cost': null
}

export default {
  components: { PanelDelimiter, AddItemButton, PanelHeader, FieldErrorMessage },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    columnHeaderClass: {
      type: String,
      default: ''
    },
    validation: {
      type: Object,
      default: null
    },
    formSubmissionAttempted: {
      type: Boolean,
      default: false
    },
    backendErrors: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    'value': {
      deep: true,
      handler () {
        this.setInitialParams()
      }
    },
    formSubmissionAttempted (newValue) {
      // Track field errors when form submission is attempted
      if (newValue) {
        this.trackFieldErrors()
      }
    },
    backendErrors: {
      deep: true,
      handler (newErrors) {
        // Track backend errors
        if (newErrors && Object.keys(newErrors).length > 0) {
          this.trackFieldErrors()
        }
      }
    }
  },
  data () {
    return {
      show: false,
      fields: [
        { key: 'specialisation_id', label: 'Specialisation', class: 'item-type-column col-4 ' + this.columnHeaderClass },
        { key: 'hours', label: 'Hours', class: 'col-4 ' + this.columnHeaderClass },
        { key: 'cost', label: 'Cost', class: 'col-4 ' + this.columnHeaderClass },
        { key: 'actions', label: '', class: 'actions-column' }
      ],
      items: [],
      fieldsWithPreviousErrors: new Map() // Track fields with errors by item index and field name
    }
  },
  computed: {
    ...mapGetters({
      getSpecialisations: 'finances/getSpecialisations'
    })
  },
  mounted () {
    this.fetchSpecialisations()
      .then(() => {
        this.setInitialParams()
      })
  },
  methods: {
    setInitialParams () {
      this.items = (this.value && this.value.length > 0) ? this.value : [cloneDeep(defaultItem)]
      this.show = this.items.length > 0
      this.triggerInputEmit()
    },
    async fetchSpecialisations () {
      return this.$api.finances.getInvoiceProjectSpecialisations()
    },
    addItem () {
      this.items.push(cloneDeep(defaultItem))
      this.show = true
      this.triggerInputEmit()
    },
    removeItem (data) {
      // Clear error tracking for removed item
      const requiredFields = ['specialisation_id', 'hours', 'cost']
      requiredFields.forEach(fieldName => {
        const errorKey = `${data.index}_${fieldName}`
        this.fieldsWithPreviousErrors.delete(errorKey)
      })

      // Re-index error tracking for remaining items
      const newErrorTracking = new Map()
      this.fieldsWithPreviousErrors.forEach((value, key) => {
        const [itemIndex, fieldName] = key.split('_')
        const indexNum = parseInt(itemIndex)
        if (indexNum > data.index) {
          // Shift index down for items after the removed one
          const newKey = `${indexNum - 1}_${fieldName}`
          newErrorTracking.set(newKey, value)
        } else if (indexNum < data.index) {
          // Keep existing index for items before the removed one
          newErrorTracking.set(key, value)
        }
        // Skip the removed item (indexNum === data.index)
      })
      this.fieldsWithPreviousErrors = newErrorTracking

      const removedIndex = data.index
      this.items = this.items.filter((item, index) => index !== removedIndex)
      this.show = this.items.length > 0
      this.triggerInputEmit()
      this.$emit('dataRemoved', { removedIndex })
    },
    triggerInputEmit () {
      this.$emit('input', this.items)
    },
    dataTouched (index, attrName) {
      this.$emit('dataTouched', { index, attrName })
    },
    togglePanel (event) {
      event.preventDefault()
      this.show = !this.show
    },
    clearBackendError (itemIndex, fieldName) {
      const errorKey = `specialisations.${itemIndex}.${fieldName}`
      if (this.backendErrors[errorKey]) {
        // Create a copy to trigger reactivity
        const updatedErrors = { ...this.backendErrors }
        delete updatedErrors[errorKey]
        this.$emit('updateBackendErrors', updatedErrors)
      }
    },
    getBackendError (itemIndex, fieldName) {
      const errorKey = `specialisations.${itemIndex}.${fieldName}`
      const error = this.backendErrors[errorKey]
      return error ? (Array.isArray(error) ? error[0] : error) : null
    },
    isSpecialisationFieldInvalid (item) {
      return this.formSubmissionAttempted && this.validation && this.validation.$error && !item.specialisation_id
    },
    isSpecialisationFieldValid (item) {
      const itemIndex = this.items.indexOf(item)
      return this.shouldShowGreenBorder(itemIndex, 'specialisation_id')
    },
    isHoursFieldInvalid (item) {
      return this.formSubmissionAttempted && this.validation && this.validation.$error && (!item.hours || item.hours <= 0)
    },
    isHoursFieldValid (item) {
      const itemIndex = this.items.indexOf(item)
      return this.shouldShowGreenBorder(itemIndex, 'hours')
    },
    isCostFieldInvalid (item) {
      return this.formSubmissionAttempted && this.validation && this.validation.$error && (!item.cost || item.cost <= 0)
    },
    isCostFieldValid (item) {
      const itemIndex = this.items.indexOf(item)
      return this.shouldShowGreenBorder(itemIndex, 'cost')
    },
    shouldShowGreenBorder (itemIndex, fieldName) {
      // Only show green border if the field had errors before and is now valid
      const hasError = this.isFieldCurrentlyInvalid(itemIndex, fieldName)

      const errorKey = `${itemIndex}_${fieldName}`
      const hadPreviousError = this.fieldsWithPreviousErrors.has(errorKey)
      const isDirty = this.validation?.$dirty

      return hadPreviousError && !hasError && isDirty && this.formSubmissionAttempted
    },
    isFieldCurrentlyInvalid (itemIndex, fieldName) {
      const item = this.items[itemIndex]
      if (!item) return false

      const backendError = this.getBackendError(itemIndex, fieldName)

      switch (fieldName) {
        case 'specialisation_id':
          return (this.formSubmissionAttempted && this.validation && this.validation.$error && !item.specialisation_id) || backendError
        case 'hours':
          return (this.formSubmissionAttempted && this.validation && this.validation.$error && (!item.hours || item.hours <= 0)) || backendError
        case 'cost':
          return (this.formSubmissionAttempted && this.validation && this.validation.$error && (!item.cost || item.cost <= 0)) || backendError
        default:
          return false
      }
    },
    trackFieldErrors () {
      // Track which fields currently have errors for each item
      const requiredFields = ['specialisation_id', 'hours', 'cost']

      this.items.forEach((item, index) => {
        requiredFields.forEach(fieldName => {
          const hasError = this.isFieldCurrentlyInvalid(index, fieldName)

          const errorKey = `${index}_${fieldName}`
          if (hasError) {
            this.fieldsWithPreviousErrors.set(errorKey, true)
          }
        })
      })
    },
    clearErrorTracking () {
      // Clear all error tracking (called when parent form is reset)
      this.fieldsWithPreviousErrors.clear()
    }
  }
}
</script>

<style lang="scss" scoped>
  .FinancesInvoicesCompanySpecialisationsTable{
    .item-type-column{
      min-width: 200px;
    }
    .hours-column,
    .cost-column {
      min-width: 110px;
    }
    .actions-column {
      min-width: 40px;
    }
    .comment-column {
      width: 100%;
    }
  }
</style>
